// app/src/features/chat/useChatWiring.js
import { useCallback, useMemo } from "react";
import { classifyPreforecastIntent } from "../../commands/classifyPreforecastIntent.js";
import { useActiveForecast } from "../../hooks/useActiveForecast.js";
import { useChatIntent } from "../../hooks/useChatIntent.js";
import { useSession } from "../../hooks/useSession.js";
import { currentChatTurn } from "../../lib/chatTurn.js";
import { captureChatTarget } from "../../lib/forecast/chatTarget.js";
import { recentConversationText } from "../../lib/forecast/conversationMemory.js";

/**
 * The chat's intent wiring: the panel actions the chat can drive, the pre-forecast answer
 * (`answerChat`) and the routing ladder (`useChatIntent`, its argument object passed unchanged —
 * "hook args unchanged"). Called where `AppInner` called `useChatIntent`: after `tourRef` and the
 * tour controller, before `useSubmitEffects` (which takes `handleChatMessage`).
 *
 * The chat primitives (`pushSayerMsg`, `pushUserMsg`, `appendEntry`) are `useChatMessages`'s
 * outputs, which `AppInner` keeps (that call takes the store mirror ref, PKG-05's); the store reaches this hook
 * as `getRunState` (injected, never imported — C-23). `autoQuestionTypeEnabled` and `multiwayEnabled`
 * are read from the session context through `useSession()`; the active forecast through
 * `useActiveForecast()`.
 *
 * @param {object} deps
 * @param {Function} deps.getRunState  the run store's live reader (injected, never imported — C-23)
 * @param {Function} deps.pushSayerMsg  useChatMessages' assistant-line primitive (AppInner's)
 * @param {Function} deps.submit  the submit command binding (useSubmitWiring's)
 */
export function useChatWiring({
  // panelActions'
  setLogOpen,
  openBugReport,
  setProOpen,
  setAccountOpen,
  setTsDrawerOpen,
  setBugOpen,
  // answerChat's
  getRunState,
  setChatThinking,
  pendingAbortsRef,
  pushSayerMsg,
  submit,
  earlierConversation,
  howSayerWorks,
  // the rest of useChatIntent's argument object
  series,
  input,
  forecastEngine,
  allEngines,
  handleBranch,
  chatCustomEventBranch,
  addSeries,
  removeSeries,
  jumpTo,
  centerToday,
  openReport,
  resetForecast,
  setDetailLevel,
  setForecastEngine,
  appendEntry,
  fitTimeline,
  setInput,
  setTourExamples,
  setTourFixture,
  pushUserMsg,
  pendingEngineRevertRef,
  pendingNewForecastRef,
  tourRef,
  tourEndConfirmRef,
  exampleRevealTimerRef,
  forecastNextRef,
  postTourTipRef,
  ninetyNotePendingRef,
  contractNotePendingRef,
}) {
  const { autoQuestionTypeEnabled, multiwayEnabled } = useSession();
  const active = useActiveForecast();
  // Panel/overlay actions the chat can drive: thin closures over the existing panel state setters,
  // kept here (not in the hook) so the hook stays store/handle-agnostic and components keep owning
  // their own state. Declared BEFORE answerChat, which dispatches them pre-forecast. Stable
  // identity: the setters are stable and openBugReport is a stable useCallback, so this memo never
  // churns.
  const panelActions = useMemo(
    () => ({
      openForecastManager: () => setLogOpen(true),
      openBugReport: () => openBugReport("chat"),
      openSettings: () => setProOpen(true),
      openAccount: () => setAccountOpen(true),
      openSeriesPanel: () => setTsDrawerOpen(true),
      closeAllPanels: () => {
        setLogOpen(false);
        setProOpen(false);
        setAccountOpen(false);
        setTsDrawerOpen(false);
        setBugOpen(false);
      },
    }),
    // All setters are stable; setBugOpen/setTsDrawerOpen come from custom hooks so eslint
    // can't infer their stability — list them (and openBugReport) to satisfy the rule.
    [openBugReport, setBugOpen, setTsDrawerOpen, setLogOpen], // eslint-disable-line react-hooks/exhaustive-deps -- setProOpen/setAccountOpen are useState setters (stable), siblings in AppInner at BASE and hook inputs here; the array is BASE's
  );

  // Pre-forecast chat handler: the routing ladder (handleChatMessage) delegates here when nothing
  // is active and the message isn't an obvious forecast. This adds intent-awareness for the
  // pre-forecast state WITHOUT touching that ladder (where the multiway routing lives). One
  // classify-then-act LLM call may route a forecast-shaped message the regexes missed into a real
  // forecast (resolving referents from the transcript), drive a UI panel, or just answer.
  const answerChat = useCallback(
    async (q) => {
      // The chat turn handleChatMessage opened for this message (lib/chatTurn.js), or null.
      // Resolves to what the user saw — "answered" | "aborted" | "error" — so the caller
      // can close the turn with it (undefined when it handed off to submit instead).
      const turn = currentChatTurn();
      // The answer goes where the question was asked (the draft), not to a forecast the user
      // opens while it is pending.
      const target = turn?.target ?? captureChatTarget(getRunState());
      setChatThinking(true);
      // Track the fetch in pendingAbortsRef so a CLEAR / reset mid-flight cancels it
      // instead of resolving later and appending a message after the UI state changed.
      const ac = new AbortController();
      pendingAbortsRef.current.add(ac);
      try {
        // Recent transcript, so referents in a follow-up resolve.
        const memory = recentConversationText(getRunState());
        const answerDone = turn?.phase("answer");
        const { parsed } = await classifyPreforecastIntent({
          question: q,
          memory,
          earlier: await earlierConversation(null, q),
          signal: ac.signal,
          autoQuestionTypeEnabled,
          howSayerWorks,
          turnId: turn?.id ?? null,
        });
        answerDone?.();
        if (ac.signal.aborted) {
          setChatThinking(false);
          return "aborted";
        }
        setChatThinking(false);
        const intent = parsed?.intent;
        // A forecast-shaped ask (incl. a resolved referent) → run it. submit() owns the
        // multiway / auto-question-type routing, so handing it the resolved question composes
        // with that work rather than duplicating it.
        if (intent === "new_forecast" && parsed?.params?.question) {
          turn?.route("submit");
          submit(parsed.params.question, undefined, false, turn);
          return;
        }
        switch (intent) {
          case "file_bug":
            panelActions.openBugReport();
            break;
          case "open_forecast_manager":
            panelActions.openForecastManager();
            break;
          case "open_settings":
            panelActions.openSettings();
            break;
          case "open_account":
            panelActions.openAccount();
            break;
          case "open_series_panel":
            panelActions.openSeriesPanel();
            break;
          case "close_panels":
            panelActions.closeAllPanels();
            break;
          default:
            break; // explain / unparsed → just post the answer below
        }
        const actedOnPanel = intent && intent !== "explain" && intent !== "new_forecast";
        pushSayerMsg(
          parsed?.response ||
            (actedOnPanel
              ? "Done."
              : "I'm Sayer — ask a yes/no question with a clear date to run a forecast, or ask me how the app works."),
          { target },
        );
        return "answered";
      } catch (e) {
        if (e.name === "AbortError") {
          setChatThinking(false);
          return "aborted";
        }
        setChatThinking(false);
        pushSayerMsg(
          "Sayer couldn't answer just now. To run a forecast, ask a yes-or-no question with a clear end date.",
          { target },
        );
        return "error";
      } finally {
        pendingAbortsRef.current.delete(ac);
      }
    },
    /* eslint-disable-line react-hooks/exhaustive-deps -- setChatThinking is a useState setter and pendingAbortsRef a useRef (stable), siblings in AppInner at BASE, and getRunState the store's module-level reader (an import at BASE); all three hook inputs here; the array is BASE's */ [
      pushSayerMsg,
      submit,
      panelActions,
      autoQuestionTypeEnabled,
      earlierConversation,
      howSayerWorks,
    ],
  );

  // Must be called AFTER useForecastSubmit/useBranchForecast so it receives submit/handleBranch
  // directly.
  const { handleChatMessage } = useChatIntent({
    active,
    series,
    input,
    forecastEngine,
    allEngines,
    multiwayEnabled,
    autoQuestionTypeEnabled,
    submit,
    handleBranch,
    handleCustomEventBranch: chatCustomEventBranch,
    addSeries,
    removeSeries,
    jumpTo,
    centerToday,
    openReport,
    resetForecast,
    setDetailLevel,
    setForecastEngine,
    appendEntry,
    fitTimeline,
    answerChat,
    earlierConversation,
    howSayerWorks,
    panels: panelActions,
    setChatThinking,
    setInput,
    setTourExamples,
    setTourFixture,
    pushSayerMsg,
    pushUserMsg,
    pendingEngineRevertRef,
    pendingAbortsRef,
    pendingNewForecastRef,
    tourRef,
    tourEndConfirmRef,
    exampleRevealTimerRef,
    forecastNextRef,
    postTourTipRef,
    ninetyNotePendingRef,
    contractNotePendingRef,
  });

  return { panelActions, answerChat, handleChatMessage };
}
